import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Check, Plug, X } from "lucide-react";
import { toast } from "sonner";
import { ChannelIcon } from "@/components/common/ChannelIcon";
import { FilterSelect } from "@/components/common/FilterSelect";
import { CHANNELS } from "@/pages/signals/rnc/rncData";
import { ChannelBadge } from "@/pages/signals/rnc/RncShared";
import { signalsApi } from "@/lib/api";
import {
  COMING_SOON_MESSAGE,
  DEFAULT_SEQUENCE_DESIGN_CHANNELS,
  UPCOMING_SIGNALS_MESSAGE,
  isActiveSignalSource,
} from "@/lib/campaignSignals";
import "@/pages/signals/rnc/rncPlatform.css";

/**
 * Multi-select channel assignment modal.
 * Pass `channelOptions` to use outreach channels instead of RNC signal sources.
 *
 * mode="assign": already-assigned stay purple/locked; only new channels can be added.
 * mode="unassign": already-assigned start purple; only those can be removed (no adds).
 */
function getNextSelection(sel, key, mode, inBaseline) {
  if (mode === "assign" && inBaseline) return null;
  if (mode === "unassign" && !inBaseline) return null;

  const next = new Set(sel);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return Array.from(next);
}

function getChannelTitleHint({ name, comingSoon, lockedAssigned, mode, inBaseline, notAssignedHere }) {
  if (comingSoon) return `${name} — coming soon`;
  if (lockedAssigned) return `${name} — already assigned`;
  if (mode === "unassign" && inBaseline) return `${name} — click to unassign`;
  if (notAssignedHere) return `${name} — not assigned`;
  return name;
}

function _getChannelState(ch, sel, baseline, mode) {
  const key = ch.key || ch.id;
  const comingSoon = Boolean(ch.disabled);
  const inBaseline = baseline.has(key);
  return {
    keyName: key,
    name: ch.name || ch.label || key,
    comingSoon,
    inBaseline,
    on: !comingSoon && sel.has(key),
    lockedAssigned: mode === "assign" && inBaseline && !comingSoon,
    notAssignedHere: mode === "unassign" && !inBaseline && !comingSoon,
  };
}

function ChannelOptionContent({ channelOptions, showSignalBadge, keyName, name, badge, lockedAssigned }) {
  if (channelOptions) {
    return (
      <>
        <span className="cb-icon" aria-hidden="true">
          {showSignalBadge ? (
            <ChannelBadge channelKey={keyName} size={20} />
          ) : (
            <ChannelIcon channel={keyName} size={16} />
          )}
        </span>
        <span className="cb-label">{name}</span>
        {badge && <span className="cb-soon">{badge}</span>}
        {lockedAssigned && !badge && <span className="cb-soon">Assigned</span>}
      </>
    );
  }
  return (
    <>
      <ChannelBadge channelKey={keyName} size={20} />
      <span className="cb-label">{name}</span>
    </>
  );
}

ChannelOptionContent.propTypes = {
  channelOptions: PropTypes.array,
  showSignalBadge: PropTypes.bool,
  keyName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  badge: PropTypes.node,
  lockedAssigned: PropTypes.bool,
};

function ChannelOptionItem({
  ch, sel, baseline, mode, channelOptions, onToggle, lookDisabled = true, useSignalBadges = false,
}) {
  const { keyName, name, comingSoon, inBaseline, on, lockedAssigned, notAssignedHere } = _getChannelState(ch, sel, baseline, mode);

  const titleHint = getChannelTitleHint({
    name,
    comingSoon,
    lockedAssigned,
    mode,
    inBaseline,
    notAssignedHere,
  });

  const className = [
    "cb-item",
    on ? "checked" : "",
    (lookDisabled && (comingSoon || notAssignedHere)) ? "disabled" : "",
    lockedAssigned ? "locked" : "",
  ].filter(Boolean).join(" ");

  const showSignalBadge = useSignalBadges || keyName === "rnc" || keyName === "custom";

  return (
    <button
      type="button"
      key={keyName}
      className={className}
      onClick={() => onToggle(keyName)}
      disabled={lookDisabled && (comingSoon || notAssignedHere)}
      title={titleHint}
      data-testid={`assign-channel-${keyName}`}
      aria-pressed={on}
    >
      <div className="cb-box">{on ? <Check size={11} strokeWidth={3} /> : null}</div>
      <ChannelOptionContent
        channelOptions={channelOptions}
        showSignalBadge={showSignalBadge}
        keyName={keyName}
        name={name}
        badge={ch.badge}
        lockedAssigned={lockedAssigned}
      />
    </button>
  );
}

ChannelOptionItem.propTypes = {
  ch: PropTypes.shape({
    key: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    badge: PropTypes.string,
  }).isRequired,
  sel: PropTypes.instanceOf(Set).isRequired,
  baseline: PropTypes.instanceOf(Set).isRequired,
  mode: PropTypes.oneOf(["assign", "unassign"]).isRequired,
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    badge: PropTypes.string,
  })),
  onToggle: PropTypes.func.isRequired,
  lookDisabled: PropTypes.bool,
  useSignalBadges: PropTypes.bool,
};

export function AssignChannelsModal({
  open,
  onOpenChange,
  campaignName,
  selected,
  onChange,
  onSave,
  channelOptions,
  title = "Assign Signals",
  description,
  mode = "assign",
  baselineChannels = [],
  saveLabel,
  conflictWarning = null,
  isOptionAllowed = null,
  unsupportedMessage = UPCOMING_SIGNALS_MESSAGE,
  useSignalBadges = false,
  listAriaLabel = "Channels",
  /** When true, Save is disabled if nothing is selected (matches create-time mandatory signals). */
  requireSelection = true,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;
  const options = channelOptions || CHANNELS;
  const sel = new Set(selected);
  const baseline = new Set(baselineChannels);
  const gateUnsupported = typeof isOptionAllowed === "function";
  const hasSelection = gateUnsupported
    ? selected.some((key) => isOptionAllowed(key))
    : selected.length > 0;
  const saveDisabled = requireSelection && !hasSelection;

  const toggle = (key) => {
    if (gateUnsupported && !isOptionAllowed(key)) {
      toast.message(unsupportedMessage);
      return;
    }
    const next = getNextSelection(sel, key, mode, baseline.has(key));
    if (next) onChange(next);
  };

  return (
    <div className="rnc-platform">
      <dialog
        open
        className="modal-overlay"
        aria-labelledby="assign-channels-modal-title"
      >
        <div
          className="modal-box"
          data-testid="assign-channels-modal"
          tabIndex={-1}
        >
          <div className="modal-header">
            <div>
              <h2 id="assign-channels-modal-title"><Plug size={16} /> {title}</h2>
              <p>
                {description
                  || `${campaignName || "Campaign"} — choose which signals feed RNCs into this workflow.`}
              </p>
            </div>
            <button type="button" className="modal-close" onClick={() => onOpenChange(false)} aria-label="Close"><X size={14} /></button>
          </div>
          <div className="modal-body">
            {conflictWarning ? (
              <div
                role="alert"
                data-testid="channel-conflict-warning"
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "var(--amber-50, #fffbeb)",
                  color: "var(--amber-900, #78350f)",
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: "pre-line",
                }}
              >
                {conflictWarning.trim()}
              </div>
            ) : null}
            <fieldset className="cb-list border-0 p-0 m-0 min-w-0" aria-label={listAriaLabel}>
              {options.map((ch) => (
                <ChannelOptionItem
                  key={ch.key || ch.id}
                  ch={ch}
                  sel={sel}
                  baseline={baseline}
                  mode={mode}
                  channelOptions={channelOptions}
                  onToggle={toggle}
                  lookDisabled={!gateUnsupported}
                  useSignalBadges={useSignalBadges || gateUnsupported}
                />
              ))}
            </fieldset>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn ghost" onClick={() => onOpenChange(false)}>Cancel</button>
            <button
              type="button"
              className="btn primary violet"
              onClick={onSave}
              disabled={saveDisabled}
              title={saveDisabled ? "Select at least one signal" : undefined}
              aria-disabled={saveDisabled}
              data-testid="save-channels-btn"
            >
              <Check size={14} /> {saveLabel || (mode === "unassign" ? "Save Unassign" : "Save Signals")}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}

AssignChannelsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  campaignName: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    badge: PropTypes.string,
  })),
  title: PropTypes.string,
  description: PropTypes.string,
  mode: PropTypes.oneOf(["assign", "unassign"]),
  baselineChannels: PropTypes.arrayOf(PropTypes.string),
  saveLabel: PropTypes.string,
  conflictWarning: PropTypes.string,
  isOptionAllowed: PropTypes.func,
  unsupportedMessage: PropTypes.string,
  useSignalBadges: PropTypes.bool,
  listAriaLabel: PropTypes.string,
  requireSelection: PropTypes.bool,
};

export function AskMayaModal({ open, onOpenChange, onGenerate, initialSignalSources = null }) {
  const [length, setLength] = useState(5);
  const [signalSources, setSignalSources] = useState(["rnc"]);
  const [signalOptions, setSignalOptions] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [persona, setPersona] = useState("VP Sales");
  const [industry, setIndustry] = useState("SaaS");
  const [goal, setGoal] = useState("Book demo");
  const [tone, setTone] = useState("Direct");

  useEffect(() => {
    if (!open) return undefined;
    const seeded = Array.isArray(initialSignalSources)
      ? initialSignalSources.filter((k) => isActiveSignalSource(k))
      : [];
    setSignalSources(seeded.length ? seeded : ["rnc"]);
    return undefined;
  }, [open, initialSignalSources]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setSignalsLoading(true);
      try {
        const data = await signalsApi.listCatalog();
        if (!cancelled) setSignalOptions(Array.isArray(data?.sources) ? data.sources : []);
      } catch {
        if (!cancelled) setSignalOptions([]);
      } finally {
        if (!cancelled) setSignalsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  const toggleSignal = (key) => {
    if (!isActiveSignalSource(key)) {
      toast.message(COMING_SOON_MESSAGE);
      return;
    }
    setSignalSources((prev) => (
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    ));
  };

  const handleGenerate = () => {
    const allowed = signalSources.filter((k) => isActiveSignalSource(k));
    if (!allowed.length) {
      toast.error("Select at least one available Signal source (RNC or ABM List)");
      return;
    }
    onGenerate({
      length,
      persona,
      industry,
      goal,
      tone,
      signalSources: allowed,
      channels: DEFAULT_SEQUENCE_DESIGN_CHANNELS,
    });
  };

  return (
    <div className="rnc-platform">
      <dialog
        open
        className="modal-overlay"
        aria-labelledby="ask-maya-modal-title"
      >
        <div
          className="modal-box wide"
          data-testid="ask-maya-modal"
          tabIndex={-1}
        >
          <div className="modal-header">
            <div>
              <h2 id="ask-maya-modal-title">✦ Ask Maya to design a sequence</h2>
              <p>Tell Maya about your ideal customer and goal — she&apos;ll draft the sequence and drop it on the canvas.</p>
            </div>
            <button type="button" className="modal-close" onClick={() => onOpenChange(false)} aria-label="Close"><X size={14} /></button>
          </div>
          <div className="modal-body">
            <div className="formgrid">
              <div className="formrow">
                <label htmlFor="maya-persona">Target Persona</label>
                <FilterSelect
                  id="maya-persona"
                  variant="field"
                  value={persona}
                  onChange={setPersona}
                  options={["VP Sales", "CEO", "CTO"].map((v) => ({ value: v, label: v }))}
                  aria-label="Target Persona"
                />
              </div>
              <div className="formrow">
                <label htmlFor="maya-industry">Industry</label>
                <FilterSelect
                  id="maya-industry"
                  variant="field"
                  value={industry}
                  onChange={setIndustry}
                  options={["SaaS", "Real Estate"].map((v) => ({ value: v, label: v }))}
                  aria-label="Industry"
                />
              </div>
              <div className="formrow">
                <label htmlFor="maya-goal">Goal</label>
                <FilterSelect
                  id="maya-goal"
                  variant="field"
                  value={goal}
                  onChange={setGoal}
                  options={["Book demo", "Qualify lead"].map((v) => ({ value: v, label: v }))}
                  aria-label="Goal"
                />
              </div>
              <div className="formrow">
                <label htmlFor="maya-tone">Tone</label>
                <FilterSelect
                  id="maya-tone"
                  variant="field"
                  value={tone}
                  onChange={setTone}
                  options={["Direct", "Consultative"].map((v) => ({ value: v, label: v }))}
                  aria-label="Tone"
                />
              </div>
            </div>
            <div className="formrow">
              <label htmlFor="maya-signals-group" id="maya-signals-label">Signals</label>
              <p className="text-[11px] text-slate-500 mb-1.5">
                Same Signal sources as Campaign Creation. Only RNC and ABM List are available in this version.
              </p>
              <fieldset id="maya-signals-group" className="border-0 p-0 m-0 min-w-0" aria-labelledby="maya-signals-label">
                {signalsLoading && !signalOptions.length ? (
                  <p className="text-[12px] text-slate-500">Loading Signal sources…</p>
                ) : null}
                {signalOptions.map((src) => {
                  const key = src.key;
                  const on = signalSources.includes(key);
                  const selectable = isActiveSignalSource(key);
                  return (
                    <button
                      type="button"
                      key={key}
                      className={`chipselect ${on ? "on" : ""}`}
                      onClick={() => toggleSignal(key)}
                      title={selectable ? src.name : `${src.name} — coming soon`}
                      data-testid={`maya-signal-${key}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <ChannelBadge channelKey={key} size={14} />
                        {src.name}
                      </span>
                    </button>
                  );
                })}
              </fieldset>
            </div>
            <div className="formrow">
              <label htmlFor="maya-length">Length (steps) — {length}</label>
              <input id="maya-length" type="range" min={3} max={9} value={length} onChange={(e) => setLength(Number(e.target.value))} style={{ width: "100%" }} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={() => onOpenChange(false)}>Discard</button>
            <button
              type="button"
              className="btn primary"
              onClick={handleGenerate}
              data-testid="maya-generate-btn"
            >
              ✦ Generate Sequence
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}

AskMayaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  initialSignalSources: PropTypes.arrayOf(PropTypes.string),
};

AskMayaModal.defaultProps = {
  initialSignalSources: null,
};
